import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ProtectedRoute from '../../components/auth/ProtectedRoute';
import {
  Container,
  Title,
  Alert,
  Text,
  Stack,
  Group,
  Card,
  Badge,
  Button,
  Loader,
  Paper,
  Divider,
  ActionIcon,
  Modal,
  NumberInput,
  TextInput,
  Textarea
} from '@mantine/core';
import {
  IconExclamationCircle,
  IconRefresh,
  IconCheck,
  IconX,
  IconClock,
  IconAlertCircle
} from '@tabler/icons-react';
import { api } from '../../services/api';
import { notifications } from '@mantine/notifications';

const MySpecialRequestsPage = () => {
  const { user } = useSelector(state => state.auth);
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewCounterOfferModal, setViewCounterOfferModal] = useState(false);

  useEffect(() => {
    // Check if user has PayPiggyPro subscription
    if (!user?.subscription || user.subscription.plan !== 'paypiggiepro' || user.subscription.status !== 'active') {
      setError('PayPiggyPro subscription required to view special requests');
      setLoading(false);
      return;
    }
    
    fetchMyRequests();
  }, [user]);

  const fetchMyRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getSpecialRequests();
      // Filter to only show requests made by the user (not received as a princess)
      const myRequests = response.requests?.filter(req => req.requester === user.id) || [];
      setRequests(myRequests);
    } catch (err) {
      console.error('Error fetching my special requests:', err);
      setError(err.message || 'Failed to load your special requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptCounterOffer = async (requestId) => {
    try {
      await api.acceptCounterOffer(requestId);
      notifications.show({
        title: 'Counter offer accepted',
        message: 'You have accepted the princess\'s counter offer',
        color: 'green'
      });
      fetchMyRequests();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to accept counter offer',
        color: 'red'
      });
    }
  };

  const handleRejectCounterOffer = async (requestId) => {
    try {
      await api.rejectSpecialRequest(requestId);
      notifications.show({
        title: 'Counter offer rejected',
        message: 'You have rejected the counter offer',
        color: 'orange'
      });
      fetchMyRequests();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to reject counter offer',
        color: 'red'
      });
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await api.cancelSpecialRequest(requestId);
      notifications.show({
        title: 'Request cancelled',
        message: 'Your special request has been cancelled',
        color: 'gray'
      });
      fetchMyRequests();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err.message || 'Failed to cancel request',
        color: 'red'
      });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'yellow',
      accepted: 'green',
      counter_offered: 'blue',
      rejected: 'red',
      completed: 'gray',
      cancelled: 'gray'
    };
    return colors[status] || 'gray';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const viewCounterOffer = (request) => {
    setSelectedRequest(request);
    setViewCounterOfferModal(true);
  };

  if (loading) {
    return (
      <ProtectedRoute requireAuth={true} redirectTo="/login">
        <Container size="lg" py="xl">
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <Loader size="lg" />
          </div>
        </Container>
      </ProtectedRoute>
    );
  }

  if (error === 'PayPiggyPro subscription required to view special requests') {
    return (
      <ProtectedRoute requireAuth={true} redirectTo="/login">
        <Container size="lg" py="xl">
          <Alert 
            icon={<IconExclamationCircle size="1rem" />} 
            color="yellow"
            title="PayPiggyPro Required"
          >
            <Stack>
              <Text>
                Special requests are only available to PayPiggyPro subscribers.
              </Text>
              <Button 
                variant="gradient"
                gradient={{ from: 'blue', to: 'cyan' }}
                onClick={() => navigate('/subscription')}
              >
                Upgrade to PayPiggyPro
              </Button>
            </Stack>
          </Alert>
        </Container>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute requireAuth={true} redirectTo="/login">
      <Container size="lg" py="xl">
        <Stack>
          <Group justify="space-between">
            <Title order={1}>My Special Requests</Title>
            <ActionIcon onClick={fetchMyRequests} variant="light" size="lg">
              <IconRefresh size="1.2rem" />
            </ActionIcon>
          </Group>

          {error && (
            <Alert icon={<IconAlertCircle size="1rem" />} color="red">
              {error}
            </Alert>
          )}

          {requests.length === 0 ? (
            <Paper p="xl" withBorder>
              <Stack align="center" spacing="md">
                <IconClock size={48} color="var(--mantine-color-gray-5)" />
                <Text c="dimmed" ta="center" size="lg">
                  No special requests yet
                </Text>
                <Text c="dimmed" ta="center" size="sm">
                  Visit a princess's profile to make a special request
                </Text>
                <Button onClick={() => navigate('/princesses')}>
                  Browse Princesses
                </Button>
              </Stack>
            </Paper>
          ) : (
            <Stack>
              {requests.map((request) => (
                <Card key={request.id} shadow="sm" p="lg" radius="md" withBorder>
                  <Stack>
                    <Group justify="space-between">
                      <Group>
                        <div>
                          <Text size="xs" c="dimmed">Requested from</Text>
                          <Text fw={500}>{request.princess_business_name || request.princess_username}</Text>
                        </div>
                        <Badge size="lg" color={getStatusColor(request.status)}>
                          {request.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </Group>
                      <div>
                        <Text size="xs" c="dimmed">Submitted</Text>
                        <Text size="sm">{formatDate(request.created_at)}</Text>
                      </div>
                    </Group>

                    <Divider />

                    <Group grow>
                      <div>
                        <Text size="sm" c="dimmed">Service Type</Text>
                        <Text fw={500}>{request.service_type}</Text>
                      </div>
                      <div>
                        <Text size="sm" c="dimmed">Polish Color</Text>
                        <Text fw={500}>{request.nail_polish_color || 'Not specified'}</Text>
                      </div>
                      <div>
                        <Text size="sm" c="dimmed">Pictures</Text>
                        <Text fw={500}>{request.number_of_pictures}</Text>
                      </div>
                      <div>
                        <Text size="sm" c="dimmed">Your Offer</Text>
                        <Text fw={600} size="lg" c="blue">${request.offer_price}</Text>
                      </div>
                    </Group>

                    {request.design && (
                      <div>
                        <Text size="sm" c="dimmed">Design Request</Text>
                        <Text>{request.design}</Text>
                      </div>
                    )}

                    {request.special_notes && (
                      <div>
                        <Text size="sm" c="dimmed">Special Notes</Text>
                        <Text>{request.special_notes}</Text>
                      </div>
                    )}

                    {request.status === 'counter_offered' && (
                      <Paper p="md" withBorder bg="blue.0">
                        <Stack>
                          <Group justify="space-between">
                            <Text fw={600} c="blue">Counter Offer from Princess</Text>
                            <Badge color="blue">NEW OFFER</Badge>
                          </Group>
                          <Divider />
                          <Group grow>
                            {request.counter_offer_price && (
                              <div>
                                <Text size="sm" c="dimmed">New Price</Text>
                                <Text fw={600} size="lg" c="blue">${request.counter_offer_price}</Text>
                              </div>
                            )}
                            {request.counter_number_of_pictures && (
                              <div>
                                <Text size="sm" c="dimmed">Pictures</Text>
                                <Text fw={500}>{request.counter_number_of_pictures}</Text>
                              </div>
                            )}
                            {request.counter_service_type && (
                              <div>
                                <Text size="sm" c="dimmed">Service Type</Text>
                                <Text fw={500}>{request.counter_service_type}</Text>
                              </div>
                            )}
                          </Group>
                          {request.counter_special_notes && (
                            <div>
                              <Text size="sm" c="dimmed">Princess's Notes</Text>
                              <Text>{request.counter_special_notes}</Text>
                            </div>
                          )}
                        </Stack>
                      </Paper>
                    )}

                    {request.status === 'accepted' && (
                      <Alert color="green" icon={<IconCheck size="1rem" />}>
                        Your request has been accepted! The princess will contact you soon.
                      </Alert>
                    )}

                    {request.status === 'rejected' && (
                      <Alert color="red" icon={<IconX size="1rem" />}>
                        Your request was declined by the princess.
                      </Alert>
                    )}

                    <Divider />

                    <Group>
                      {request.status === 'counter_offered' && (
                        <>
                          <Button
                            leftSection={<IconCheck size="1rem" />}
                            color="green"
                            onClick={() => handleAcceptCounterOffer(request.id)}
                          >
                            Accept Counter Offer
                          </Button>
                          <Button
                            leftSection={<IconX size="1rem" />}
                            color="red"
                            variant="light"
                            onClick={() => handleRejectCounterOffer(request.id)}
                          >
                            Reject Counter Offer
                          </Button>
                          <Button
                            variant="subtle"
                            onClick={() => viewCounterOffer(request)}
                          >
                            View Details
                          </Button>
                        </>
                      )}
                      {request.status === 'pending' && (
                        <Button
                          leftSection={<IconX size="1rem" />}
                          color="gray"
                          variant="light"
                          onClick={() => handleCancelRequest(request.id)}
                        >
                          Cancel Request
                        </Button>
                      )}
                      {request.status === 'accepted' && (
                        <Button
                          onClick={() => navigate(`/messages/${request.princess}`)}
                        >
                          Message Princess
                        </Button>
                      )}
                    </Group>
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </Stack>

        <Modal
          opened={viewCounterOfferModal}
          onClose={() => setViewCounterOfferModal(false)}
          title="Counter Offer Details"
          size="lg"
        >
          {selectedRequest && (
            <Stack>
              <Title order={4}>Original Request</Title>
              <Paper p="md" withBorder>
                <Group grow>
                  <div>
                    <Text size="sm" c="dimmed">Your Offer</Text>
                    <Text fw={500}>${selectedRequest.offer_price}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Pictures</Text>
                    <Text fw={500}>{selectedRequest.number_of_pictures}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Service</Text>
                    <Text fw={500}>{selectedRequest.service_type}</Text>
                  </div>
                </Group>
              </Paper>

              <Title order={4}>Princess's Counter Offer</Title>
              <Paper p="md" withBorder bg="blue.0">
                <Group grow>
                  <div>
                    <Text size="sm" c="dimmed">New Price</Text>
                    <Text fw={600} size="lg" c="blue">
                      ${selectedRequest.counter_offer_price || selectedRequest.offer_price}
                    </Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Pictures</Text>
                    <Text fw={500}>
                      {selectedRequest.counter_number_of_pictures || selectedRequest.number_of_pictures}
                    </Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Service</Text>
                    <Text fw={500}>
                      {selectedRequest.counter_service_type || selectedRequest.service_type}
                    </Text>
                  </div>
                </Group>
                {selectedRequest.counter_special_notes && (
                  <div style={{ marginTop: '1rem' }}>
                    <Text size="sm" c="dimmed">Princess's Notes</Text>
                    <Text>{selectedRequest.counter_special_notes}</Text>
                  </div>
                )}
              </Paper>

              <Group justify="flex-end">
                <Button variant="subtle" onClick={() => setViewCounterOfferModal(false)}>
                  Close
                </Button>
                <Button
                  leftSection={<IconCheck size="1rem" />}
                  color="green"
                  onClick={() => {
                    handleAcceptCounterOffer(selectedRequest.id);
                    setViewCounterOfferModal(false);
                  }}
                >
                  Accept Counter Offer
                </Button>
              </Group>
            </Stack>
          )}
        </Modal>
      </Container>
    </ProtectedRoute>
  );
};

export default MySpecialRequestsPage;